import { mulberry32, type Rng, type Structure } from '@nine/shared';
import { box, canvas, ctx2d, ell, outline } from './pixel';
import { buildingSprite } from './buildings';
import { kitSprite } from './kit';
import { structureLook } from './layoutLook';
import { TERRACE_STYLES, terraceSprite } from './terraces';

/**
 * Procedural scenery sprites: trees and buildings, drawn in code like the characters.
 * Every sprite comes with an anchor: the pixel that sits on the map point it stands on (a tree's
 * trunk base; a building's north footprint corner).
 */
export interface Sprite {
  img: HTMLCanvasElement;
  ax: number;
  ay: number;
}

export type Pt = [number, number];

/** Alpha snapped to fully on or off, so filled polygons keep hard pixel edges. */
export function crisp(c: HTMLCanvasElement): HTMLCanvasElement {
  const g = ctx2d(c);
  const img = g.getImageData(0, 0, c.width, c.height);
  const s = img.data;
  for (let i = 3; i < s.length; i += 4) s[i] = s[i]! >= 110 ? 255 : 0;
  g.putImageData(img, 0, 0);
  return c;
}

export function poly(g: CanvasRenderingContext2D, pts: Pt[], col: string): void {
  g.fillStyle = col;
  g.beginPath();
  pts.forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.closePath();
  g.fill();
}

export function seg(g: CanvasRenderingContext2D, a: Pt, b: Pt, col: string, w = 1): void {
  g.strokeStyle = col;
  g.lineWidth = w;
  g.beginPath();
  g.moveTo(a[0], a[1]);
  g.lineTo(b[0], b[1]);
  g.stroke();
}

/**
 * Ground shadow drawn into a sprite after outlining (so it gets no outline), behind what is there:
 * pixel art like the figures' (render/contactShadow.ts), cool, a solid core and a dithered rim.
 */
function footShadow(c: HTMLCanvasElement, x: number, y: number, rx: number, ry: number): void {
  const g = ctx2d(c);
  g.save();
  g.globalCompositeOperation = 'destination-over';
  g.fillStyle = 'rgba(22,20,40,.3)';
  const X0 = Math.round(x);
  const Y0 = Math.round(y);
  for (let j = -ry; j <= ry; j++)
    for (let i = -rx; i <= rx; i++) {
      const d = (i / (rx + 0.5)) ** 2 + (j / (ry + 0.5)) ** 2;
      if (d > 1 || (d > 0.6 && (i + j) & 1)) continue;
      g.fillRect(X0 + i, Y0 + j, 1, 1);
    }
  g.restore();
}

/** A shaded leafy blob: dark underside, mid body, sunlit upper-right, with speckled leaves. */
function canopy(
  g: CanvasRenderingContext2D,
  r: Rng,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  pal: readonly [string, string, string, string],
): void {
  ell(g, cx, cy + 1, rx, ry, pal[0]);
  ell(g, cx + 1, cy - 1, rx - 1.5, ry - 1.5, pal[1]);
  ell(g, cx + rx * 0.3, cy - ry * 0.35, rx * 0.55, ry * 0.5, pal[2]);
  const n = Math.round(rx * ry * 0.18);
  for (let k = 0; k < n; k++) {
    const a = r() * Math.PI * 2;
    const d = Math.sqrt(r());
    const x = Math.round(cx + Math.cos(a) * rx * d * 0.9);
    const y = Math.round(cy + Math.sin(a) * ry * d * 0.9);
    const lit = x > cx && y < cy;
    box(g, x, y, 1, 1, lit ? pal[3] : r() < 0.5 ? pal[0] : pal[2]);
  }
}

/** A trunk drawn along a polyline, tapering, with a lit right edge (toward the key light) and bark flecks. */
function trunk(
  g: CanvasRenderingContext2D,
  r: Rng,
  pts: Pt[],
  w0: number,
  w1: number,
  col: string,
  lit: string,
): void {
  const total = pts.length - 1;
  for (let s = 0; s < total; s++) {
    const [x0, y0] = pts[s]!;
    const [x1, y1] = pts[s + 1]!;
    const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0)));
    for (let k = 0; k <= n; k++) {
      const f = (s + k / n) / total;
      const w = Math.max(1, Math.round(w0 + (w1 - w0) * f));
      const x = Math.round(x0 + ((x1 - x0) * k) / n - w / 2);
      const y = Math.round(y0 + ((y1 - y0) * k) / n);
      box(g, x, y, w, 1, col);
      const lw = Math.max(1, Math.round(w * 0.3));
      box(g, x + w - lw, y, lw, 1, lit);
      if (r() < 0.12 && w > 2) box(g, x + 1 + Math.floor(r() * (w - 1)), y, 1, 1, '#2a1c14');
    }
  }
}

// ───────────────────────── trees ─────────────────────────

/**
 * A pad of pine needles as in ink paintings: flat underneath, a tufted comb of needles along the
 * top edge, lit from the upper right.
 */
function needlePad(
  g: CanvasRenderingContext2D,
  r: Rng,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  // C0-LOOK P4: five greens by value: deep underside, shade, body, lit, sun accent (warm yellow-green).
  const DEEP = '#13291f';
  const SHADE = '#1f4636';
  const BODY = '#2f6048';
  const LIT = '#4f8d64';
  const SUN = '#86bf7a';
  for (let x = -rx; x <= rx; x++) {
    const f = 1 - (x / rx) * (x / rx);
    if (f <= 0) continue;
    const tuft = r() < 0.35 ? 2 : r() < 0.5 ? 1 : 0;
    const top = Math.round(cy - ry * Math.sqrt(f) - tuft);
    const bottom = Math.round(cy + ry * 0.45 * Math.sqrt(f));
    const px = Math.round(cx + x);
    const lit = x > -rx * 0.25;
    const h = bottom - top + 1;
    // body, with the lower third in shade and a dithered join between them
    box(g, px, top, 1, h, lit ? BODY : SHADE);
    const shadeFrom = bottom - Math.max(2, Math.round(h * 0.4));
    box(g, px, shadeFrom, 1, bottom - shadeFrom + 1, lit ? SHADE : DEEP);
    if (shadeFrom - 1 > top && (px & 1) === 0) box(g, px, shadeFrom - 1, 1, 1, lit ? SHADE : DEEP);
    box(g, px, bottom, 1, 1, DEEP);
    // light on the crown of the pad: right side brightest
    box(g, px, top, 1, 2, lit ? LIT : BODY);
    if (lit && x > rx * 0.1 && r() < 0.55) box(g, px, top, 1, 1, SUN);
    if (lit && r() < 0.3) box(g, px, top + 2, 1, 1, LIT);
    // needle clumps: small light flecks in the upper half, dark flecks below
    if (r() < 0.16 && h > 5) box(g, px, top + 2 + Math.floor(r() * Math.max(1, h * 0.35)), 2, 1, lit ? LIT : BODY);
    if (r() < 0.14 && h > 5) box(g, px, shadeFrom + 1, 1, 1, DEEP);
    if (r() < 0.12) box(g, px, top - 1, 1, 1, lit ? BODY : SHADE);
  }
}

/** Chinese pine (松): a scaly red-brown trunk, crooked branches and layered, flat needle pads. */
function pine(seed: number): Sprite {
  const W = 92;
  const H = 116;
  const c = canvas(W, H);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  const flip = seed % 2 ? -1 : 1;
  const X = (x: number) => 46 + x * flip;
  const bark = '#6a3e2a';
  const barkLit = '#9a6040';
  trunk(
    g,
    r,
    [
      [X(0), H - 4],
      [X(-4), H - 34],
      [X(3), H - 60],
      [X(-2), H - 84],
      [X(1), H - 100],
    ],
    9,
    3,
    bark,
    barkLit,
  );
  // Scales on the trunk.
  for (let k = 0; k < 18; k++)
    box(g, X(-3 + Math.floor(r() * 6)), H - 8 - Math.floor(r() * 60), 2, 1, '#4a2a1c');
  const pads: [number, number, number, number][] = [
    [-26, -44, 18, 6],
    [24, -58, 20, 6],
    [-18, -72, 16, 5],
    [16, -84, 15, 5],
    [-6, -98, 12, 5],
    [4, -50, 10, 4],
  ];
  for (const [x, y] of pads)
    trunk(
      g,
      r,
      [
        [X(Math.sign(x) * 2), H + y + 10],
        [X(x * 0.7), H + y + 3],
      ],
      3,
      2,
      bark,
      barkLit,
    );
  for (const [x, y, rx, ry] of pads) needlePad(g, r, X(x), H + y, rx, ry);
  outline(c, '#0e1a15');
  footShadow(c, X(0), H - 4, 22, 5);
  return { img: c, ax: X(0), ay: H - 4 };
}

/** Tiered pine (C0-LOOK P4): a trunk and 6 stacked skirts, each in 4 greens, sun on the upper-right slopes. */
function cypress(seed: number): Sprite {
  const W = 40;
  const H = 120;
  const c = canvas(W, H);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  const DEEP = '#13291f';
  const SHADE = '#1f4636';
  const BODY = '#2f6048';
  const LIT = '#4f8d64';
  const SUN = '#86bf7a';
  trunk(
    g,
    r,
    [
      [20, H - 4],
      [20, H - 24],
    ],
    5,
    4,
    '#5a3a2a',
    '#7e5640',
  );
  const tiers = 6;
  for (let k = 0; k < tiers; k++) {
    const apex = 4 + k * 15;
    const base = apex + 24;
    const maxHw = 6 + k * 2.4;
    for (let y = apex; y <= base; y++) {
      const t = (y - apex) / (base - apex);
      const hw = Math.max(1, Math.round(maxHw * Math.pow(t, 0.9) + (r() < 0.35 ? 1 : 0)));
      // left (shade) half, right (lit) half
      box(g, 20 - hw, y, hw, 1, y > base - 3 ? DEEP : SHADE);
      box(g, 20, y, hw, 1, y > base - 3 ? SHADE : BODY);
      // sun on the right slope edge, and a lit streak on the upper part
      if (y < base - 3) {
        box(g, 20 + hw - 2, y, 2, 1, LIT);
        if (r() < 0.45) box(g, 20 + hw - 1, y, 1, 1, SUN);
        if (t < 0.5 && r() < 0.5) box(g, 20 + Math.round(hw * 0.3), y, 1, 1, LIT);
      }
      // ragged lower edge: dark needle tips hang below the base
      if (y === base) for (let x = -hw; x < hw; x += 2) box(g, 20 + x, base + 1, 1, r() < 0.5 ? 2 : 1, DEEP);
      if (r() < 0.12) box(g, 20 - hw + Math.floor(r() * hw * 2), y, 1, 1, DEEP);
    }
  }
  outline(c, '#0c1612');
  footShadow(c, 20, H - 4, 14, 4);
  return { img: c, ax: 20, ay: H - 4 };
}

/** Blossom tree (C0-LOOK P4): a round pink crown in lumpy clusters, three pinks, brown trunk. */
function plum(seed: number): Sprite {
  const W = 72;
  const H = 92;
  const c = canvas(W, H);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  const bark = '#4a2e26';
  trunk(
    g,
    r,
    [
      [36, H - 4],
      [34, H - 24],
      [37, H - 42],
    ],
    7,
    4,
    bark,
    '#7a5042',
  );
  for (const [x2, y2] of [
    [22, -56],
    [50, -58],
    [36, -66],
  ] as const)
    trunk(
      g,
      r,
      [
        [36, H - 40],
        [x2, H + y2],
      ],
      3,
      2,
      bark,
      '#7a5042',
    );
  const pal = ['#a9486c', '#d9708f', '#f2a0b9', '#ffd9e4'] as const;
  canopy(g, r, 36, 36, 25, 18, pal);
  canopy(g, r, 20, 48, 14, 10, pal);
  canopy(g, r, 52, 46, 14, 10, pal);
  canopy(g, r, 36, 22, 15, 10, pal);
  outline(c, '#3a1526');
  footShadow(c, 36, H - 4, 18, 5);
  return { img: c, ax: 36, ay: H - 4 };
}

/** Weeping willow: a soft crown and long strands hanging almost to the ground. */
function willow(seed: number): Sprite {
  const W = 66;
  const H = 104;
  const c = canvas(W, H);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  trunk(
    g,
    r,
    [
      [33, H - 4],
      [31, H - 40],
      [35, H - 64],
    ],
    7,
    4,
    '#4e3a2c',
    '#7a5c42',
  );
  const pal = ['#44663a', '#5c8046', '#789e5c', '#9cbb7c'] as const;
  canopy(g, r, 33, 34, 22, 15, pal);
  canopy(g, r, 22, 42, 12, 9, pal);
  canopy(g, r, 45, 42, 12, 9, pal);
  // Hanging strands, swaying a little.
  for (let k = 0; k < 26; k++) {
    const x0 = 12 + Math.floor(r() * 43);
    const y0 = 38 + Math.floor(r() * 10);
    const len = 24 + Math.floor(r() * 30);
    const sway = (r() - 0.5) * 3;
    for (let q = 0; q < len; q++) {
      const x = Math.round(x0 + Math.sin(q / 9) * sway);
      box(g, x, y0 + q, 1, 1, q % 7 === 3 ? '#9cbb7c' : k % 3 ? '#5c8046' : '#4a6e3c');
    }
  }
  outline(c, '#1a2612');
  footShadow(c, 33, H - 4, 20, 5);
  return { img: c, ax: 33, ay: H - 4 };
}

/** Clump of bamboo: stalks of different heights with nodes and leaf sprays. */
function bamboo(seed: number): Sprite {
  const W = 44;
  const H = 110;
  const c = canvas(W, H);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  for (const [x, top] of [
    [11, 16],
    [18, 4],
    [25, 10],
    [31, 22],
    [15, 32],
    [28, 36],
  ] as const) {
    box(g, x, top, 3, H - 6 - top, '#5f8f3e');
    box(g, x + 2, top, 1, H - 6 - top, '#8fc066');
    box(g, x, top, 1, H - 6 - top, '#4a7430');
    for (let y = top + 8; y < H - 8; y += 11) box(g, x - 1, y, 5, 1, '#3e6428');
    for (let k = 0; k < 5; k++) {
      const ly = top + 2 + Math.floor(r() * 48);
      const dir = r() < 0.5 ? -1 : 1;
      for (let q = 0; q < 7; q++)
        box(g, x + 1 + dir * (q + 1), ly + Math.floor(q * 0.45), 2, 1, q > 4 ? '#7cb052' : '#4f8a36');
    }
  }
  outline(c, '#1c2a14');
  footShadow(c, 22, H - 5, 16, 4);
  return { img: c, ax: 22, ay: H - 5 };
}

export type TreeKind = 'pine' | 'cypress' | 'bamboo' | 'plum' | 'willow';

/** Every tree sprite, a few variants of each. */
/**
 * Snow on top of a sprite: on every upward-facing edge (just below where the sprite starts going
 * down a column), the first few pixels turn snow white, shaded blue on the left (away from the key light).
 */
export function snowify(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = canvas(src.width, src.height);
  const g = ctx2d(c);
  g.drawImage(src, 0, 0);
  const img = g.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  const W = c.width;
  for (let x = 0; x < W; x++) {
    let open = true;
    for (let y = 0; y < c.height; y++) {
      const a = d[(y * W + x) * 4 + 3]!;
      if (a && open) {
        // Skip the outline pixel, then lay 2–3 px of snow.
        const depth = 2 + ((x * 7) % 3 === 0 ? 1 : 0);
        for (let k = 1; k <= depth; k++) {
          const i = ((y + k) * W + x) * 4;
          if (y + k >= c.height || !d[i + 3]) break;
          const shade = x < W * 0.45 ? 0.86 : 1;
          d[i] = 236 * shade;
          d[i + 1] = 242 * shade;
          d[i + 2] = 250;
        }
      }
      open = !a;
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}

/** The trees of the snow peak: pines and cypresses only, capped with snow. */
export function snowTreeSprites(): Record<TreeKind, Sprite[]> {
  const cap = (list: Sprite[]) => list.map((s) => ({ ...s, img: snowify(s.img) }));
  const pines = cap([pine(3), pine(4), pine(9), pine(10)]);
  const cypresses = cap([cypress(17), cypress(18)]);
  return { pine: pines, cypress: cypresses, bamboo: pines, plum: cypresses, willow: pines };
}

export function treeSprites(): Record<TreeKind, Sprite[]> {
  return {
    pine: [pine(3), pine(4), pine(9), pine(10)],
    cypress: [cypress(17), cypress(18)],
    bamboo: [bamboo(57), bamboo(58)],
    plum: [plum(61), plum(62)],
    willow: [willow(21), willow(22), willow(23)],
  };
}

/** Which tree grows on a tile, from a hash of its map position: mostly pines and bamboo. */
export function treeKindAt(h: number): TreeKind {
  return h < 0.45 ? 'pine' : h < 0.7 ? 'bamboo' : h < 0.84 ? 'cypress' : h < 0.94 ? 'plum' : 'willow';
}

// ───────────────────────── buildings ─────────────────────────

/** Iso projection inside a building sprite: footprint units (x, y) and height z in pixels. */
export type Proj = (x: number, y: number, z: number) => Pt;

export const shade = (hex: string, m: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v * m)));
  return `rgb(${ch(n >> 16)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
};

/** A box (x0..x1 × y0..y1 × z0..z1): top, front-left (y = y1) and front-right (x = x1) faces. */
export function block3(
  g: CanvasRenderingContext2D,
  P: Proj,
  [x0, y0, x1, y1]: readonly [number, number, number, number],
  z0: number,
  z1: number,
  col: string,
): void {
  poly(g, [P(x0, y1, z0), P(x1, y1, z0), P(x1, y1, z1), P(x0, y1, z1)], shade(col, 0.8));
  poly(g, [P(x1, y0, z0), P(x1, y1, z0), P(x1, y1, z1), P(x1, y0, z1)], shade(col, 1));
  poly(g, [P(x0, y0, z1), P(x1, y0, z1), P(x1, y1, z1), P(x0, y1, z1)], shade(col, 1.15));
}

/** A sprite drawn along x, turned to run along y (its mirror image) when that is its axis. */
export function alongAxis(s: Sprite, axis: 'x' | 'y'): Sprite {
  if (axis === 'x') return s;
  const { width: W, height: H } = s.img;
  const m = canvas(W, H);
  const mg = ctx2d(m);
  mg.translate(W, 0);
  mg.scale(-1, 1);
  mg.drawImage(s.img, 0, 0);
  return { img: m, ax: W - s.ax, ay: s.ay };
}

/** The sprite for a building, cached by its look. */
export function structureSprite(raw: Structure, cache: Map<string, Sprite>): Sprite {
  // The plains building kit draws the 256 m plains' structures as themselves (render/kit, lane C0-PROPS).
  const kit = kitSprite(raw, cache);
  if (kit) return kit;
  // An authored layout's kinds are drawn as the nearest existing sprite (layoutLook.ts).
  const st = structureLook(raw) ?? raw;
  const key = `${st.kind}:${st.w}x${st.h}:${st.variant}:${st.axis ?? ''}:${st.facing ?? ''}:${JSON.stringify(st.stairs ?? [])}`;
  let s = cache.get(key);
  if (!s) {
    s =
      st.kind === 'terrace'
        ? terraceSprite(TERRACE_STYLES[st.variant % TERRACE_STYLES.length]!, {
            len: st.facing === 'e' || st.facing === 'w' ? st.h : st.w,
            stairs: st.stairs ?? [],
            facing: st.facing ?? 's',
          })
        : buildingSprite(st.kind, st.w, st.h, st.variant, st.axis ?? 'x');
    cache.set(key, s);
  }
  return s;
}
